import './src/tailwind.css';
import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import { installGlobalErrorReporting, reportClientError } from './src/utils/reportError';
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { UserProvider } from './contexts/UserContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { CartProvider } from './contexts/CartContext';
import { MenuProvider } from './src/contexts/MenuContext';
import { SettingsProvider } from './src/contexts/SettingsContext';

// Sentry инициализируется только если задан VITE_SENTRY_DSN в .env.
// В dev-режиме по умолчанию выключен, чтобы не засорять проект тестовыми ошибками.
const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    // Сэмплирование: в проде логируем все ошибки и 10% транзакций performance.
    // Free tier Sentry = 5K ошибок / 10K транзакций в месяц — хватит с запасом.
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
    // Ignore носинг мусор
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
    ],
  });
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

// Ловушки для того, что не поймает React-boundary (async, промисы).
installGlobalErrorReporting();

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <Sentry.ErrorBoundary
      onError={(error, componentStack) => reportClientError('boundary', error, componentStack)}
      fallback={({ error }) => {
        // ChunkLoadError / "Failed to fetch dynamically imported module" —
        // классический stale-SW баг после deploy. Решение: убить SW + hard
        // reload. Это автоматически вернёт пользователя в рабочее состояние.
        const msg = String((error as any)?.message || error || '');
        const isChunkError = /dynamically imported|ChunkLoad|Loading chunk|Importing a module/i.test(msg);
        const hardReload = () => {
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.getRegistrations().then((regs) => {
              Promise.all(regs.map((r) => r.unregister())).finally(() => {
                window.location.reload();
              });
            });
          } else {
            window.location.reload();
          }
        };
        // Авто-reload для chunk-error: без вмешательства пользователя.
        if (isChunkError && typeof window !== 'undefined' && !sessionStorage.getItem('chunk_reload_attempted')) {
          sessionStorage.setItem('chunk_reload_attempted', '1');
          setTimeout(hardReload, 100);
        }
        return (
          <div style={{ padding: 24, fontFamily: 'system-ui', maxWidth: 480, margin: '40px auto' }}>
            <h2 style={{ marginBottom: 8 }}>Что-то пошло не так</h2>
            <p style={{ color: '#666', marginBottom: 16 }}>
              {isChunkError
                ? 'Загружаем обновлённую версию…'
                : 'Мы уже в курсе. Попробуйте обновить страницу.'}
            </p>
            <pre style={{ color: '#888', fontSize: 11, whiteSpace: 'pre-wrap', wordBreak: 'break-word', marginBottom: 16 }}>{msg.slice(0, 200)}</pre>
            <button
              onClick={hardReload}
              style={{
                padding: '10px 20px',
                background: '#EA6100',
                color: '#fff',
                border: 'none',
                borderRadius: 8,
                fontWeight: 700,
                cursor: 'pointer',
              }}
            >
              Обновить страницу
            </button>
          </div>
        );
      }}
    >
      <BrowserRouter>
        <UserProvider>
          <ThemeProvider>
            <LanguageProvider>
              {/* Настройки выше корзины: корзине нужен флаг тестового режима,
                  чтобы помечать пробные заказы. Ни настройки, ни меню от
                  корзины не зависят, поэтому порядок безопасен. */}
              <SettingsProvider>
                  <MenuProvider>
                      <CartProvider>
                          <App />
                      </CartProvider>
                  </MenuProvider>
              </SettingsProvider>
            </LanguageProvider>
          </ThemeProvider>
        </UserProvider>
      </BrowserRouter>
    </Sentry.ErrorBoundary>
  </React.StrictMode>
);
